import React, { Children, Fragment } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, ChevronDown, X } from 'lucide-react';
import { BlueprintGrid } from '../components/BlueprintGrid';
import { TopographicPattern } from '../components/TopographicPattern';
import { BrandRays } from '../components/BrandRays';
export function About() {
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const stagger = {
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  return <main className="w-full" data-id="element-198">
      {/* HERO */}
      <section className="relative py-20 px-6 bg-paper pt-32 overflow-hidden" data-id="element-199">
        <div className="absolute inset-0 text-seaglass/[0.15]" data-id="element-200">
          <BlueprintGrid data-id="element-201" />
        </div>

        <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-4xl mx-auto relative z-10" data-id="element-202">
          <motion.h1 variants={fadeIn} className="text-4xl md:text-6xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-203">
            I built this perspective from the inside.
          </motion.h1>

          <motion.div variants={fadeIn} className="text-lg text-text max-w-2xl mb-8 space-y-4" data-id="element-204">
            <p data-id="element-205">
              For 15+ years, I worked inside the parts of the business where
              growth friction actually builds: growth, revenue, operations,
              systems, delivery, and executive leadership. I kept seeing the
              same pattern.
            </p>
            <p className="font-medium text-navy" data-id="element-206">
              The business was still working. But it was getting heavier,
              slower, and more dependent than it should have been. That is where
              this offer comes from.
            </p>
          </motion.div>

          {/* Hero CTAs - Side by side on desktop, stacked on mobile */}
          <motion.div variants={fadeIn} className="flex flex-col sm:flex-row sm:items-start gap-6 mb-10" data-id="element-207">
            {/* Primary CTA with microcopy */}
            <div className="flex flex-col" data-id="element-208">
              <Link to="/book" className="inline-flex items-center space-x-2 px-8 py-4 bg-sand border-2 border-sand text-navy font-medium hover:bg-white transition-all duration-300" data-id="element-209">
                <span data-id="element-210">Book an Honest Conversation</span>
                <ArrowRight className="w-5 h-5" data-id="element-211" />
              </Link>
              <motion.p variants={fadeIn} className="text-sm text-text-muted mt-2" data-id="element-212">
                45 minutes. No pitch. Just clarity.
              </motion.p>
            </div>

            {/* Secondary link with microcopy */}
            <div className="flex flex-col" data-id="element-213">
              <a href="#proof" className="inline-flex items-center space-x-2 px-6 py-4 border border-navy/20 hover:border-navy/40 text-navy/70 hover:text-navy font-medium transition-all duration-300" data-id="element-214">
                <span data-id="element-215">See operating proof</span>
                <ChevronDown className="w-4 h-4" data-id="element-216" />
              </a>
              <motion.p variants={fadeIn} className="text-sm text-text-muted mt-2" data-id="element-217">
                Read the full background below
              </motion.p>
            </div>
          </motion.div>
        </motion.div>

        {/* Scroll cue */}
        <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center" animate={{
        y: [0, 6, 0]
      }} transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-218">
          <ChevronDown className="w-5 h-5 text-seaglass/50" data-id="element-219" />
        </motion.div>
      </section>

      {/* WHERE THIS PERSPECTIVE COMES FROM */}
      <section className="py-24 px-6 grain border-t hairline-top bg-paper-dark" data-id="element-220">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-221">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-222">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-223">
              Where this perspective comes from
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6" data-id="element-224">
            <p className="text-body text-text" data-id="element-225">
              I have worked across the front end that creates demand, the middle
              that converts it, and the back end that has to fulfill what was
              sold.
            </p>

            <p className="text-body text-text font-medium text-navy" data-id="element-226">
              That range matters because the real bottleneck is rarely sitting
              neatly inside one department.
            </p>

            <p className="text-body text-text" data-id="element-227">
              It usually lives in the seams:
            </p>

            <ul className="space-y-3 pl-1" data-id="element-228">
              {['where promise and fulfillment stop lining up', 'where handoffs break under load', 'where decisions do not resolve cleanly', 'where the numbers need interpretation before anyone trusts them', 'where the founder becomes the router, translator, or safety net'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-229">
                  <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-230" />
                  <span className="text-body text-text" data-id="element-231">{item}</span>
                </li>)}
            </ul>

            <p className="text-body text-text" data-id="element-232">
              That is why this offer is built around finding the real source of
              the drag, not just reacting to the symptom everyone can already
              see.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* BUILT FROM THE INSIDE */}
      <section id="proof" className="py-24 px-6 bg-paper" data-id="element-233">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-234">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-235">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-236">
              What those environments taught me to see
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6" data-id="element-237">
            <p className="text-body text-text" data-id="element-238">
              At Tom Ferry, I worked across marketing, revenue operations,
              systems, events, and executive leadership as the business scaled
              from roughly $6M to $84M.
            </p>

            <p className="text-body text-text" data-id="element-239">
              The lesson was not about growth tactics. It was that every stage
              of growth breaks a different part of the operating logic — and the
              fix is never the same twice. What worked at $10M created drag at
              $40M. What felt like a people problem was usually trapped decision
              logic.
            </p>

            <p className="text-body text-text" data-id="element-240">
              Later, as Chief Growth Officer, the work shifted to efficiency
              under scale — ARR grew from $60.9M to $73.3M while premium tiers
              added $3.5M in six months. Earlier, in revenue operations,
              redesigning the system behind the motion drove a 24% increase in
              unit sales and 117% increase in net recurring revenue.
            </p>

            <p className="text-body text-text" data-id="element-241">
              At Jon Gordon Companies, I served as President. The work there was
              not more strategy. It was translating founder vision into
              workflows, priorities, and operating structure the team could
              actually run. That is where the independence principle came from:
              if the solution still depends on me sitting in the middle, it is
              not done.
            </p>

            <div className="border-l-2 border-seaglass pl-8 py-2 mt-4" data-id="element-242">
              <p className="text-body text-navy font-medium" data-id="element-243">
                When capable teams struggle, the issue is usually not execution
                — it is that the system does not tell them how to win.
              </p>
            </div>

            <p className="text-body text-text mt-4" data-id="element-244">
              That is the throughline across every environment: find the hidden
              source of the load, fix the logic underneath it, and leave the
              team with something they can run.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* WHAT I ACTUALLY CHANGE */}
      <section className="py-24 px-6 bg-paper-dark grain" data-id="element-245">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-246">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-247">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-248">
              What I actually change
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6" data-id="element-249">
            <p className="text-body text-text" data-id="element-250">
              I am usually not brought in because the company needs more ideas.
              I am brought in because too much still depends on interpretation,
              escalation, and rescue.
            </p>

            <p className="text-body text-text" data-id="element-251">The work usually looks like:</p>

            <ul className="space-y-3 pl-1" data-id="element-252">
              {['clarifying ownership where work keeps bouncing', 'tightening decision rights and escalation rules', 'cleaning up handoffs that break under real load', 'aligning definitions so leaders can trust the signal', 'containing and pricing exceptions instead of letting them quietly spread', 'redesigning one important part of the business so the team can run it without constant founder intervention'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-253">
                  <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-254" />
                  <span className="text-body text-text" data-id="element-255">{item}</span>
                </li>)}
            </ul>

            <p className="text-body text-navy font-medium mt-6" data-id="element-256">
              That is how decisions start resolving lower, managers carry more
              of the load, and fewer problems route back through the founder.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* HOW I WORK */}
      <section className="py-24 px-6 bg-paper" data-id="element-257">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-258">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-259">
            <h2 className="text-3xl md:text-4xl font-serif font-semibold text-navy mb-8" data-id="element-260">
              How I work
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-8 text-body" data-id="element-261">
            <p className="text-text mb-6" data-id="element-262">
              A few principles tend to guide my work:
            </p>

            <div className="space-y-6" data-id="element-263">
              <div data-id="element-264">
                <h3 className="text-lg font-semibold text-navy mb-2" data-id="element-265">
                  Clarity before motion
                </h3>
                <p className="text-text" data-id="element-266">
                  I do not push activity before the problem is clear.
                </p>
              </div>

              <div data-id="element-267">
                <h3 className="text-lg font-semibold text-navy mb-2" data-id="element-268">
                  Systems over one-offs
                </h3>
                <p className="text-text" data-id="element-269">
                  If the answer depends on heroic follow-through every time, it
                  is not fixed.
                </p>
              </div>

              <div data-id="element-270">
                <h3 className="text-lg font-semibold text-navy mb-2" data-id="element-271">
                  Math beats vibes
                </h3>
                <p className="text-text" data-id="element-272">
                  The economics matter. Margin, capacity, load, and tradeoffs
                  matter.
                </p>
              </div>

              <div data-id="element-273">
                <h3 className="text-lg font-semibold text-navy mb-2" data-id="element-274">
                  One constraint at a time
                </h3>
                <p className="text-text" data-id="element-275">
                  I would rather remove one real bottleneck than create a
                  backlog of smart observations.
                </p>
              </div>

              <div data-id="element-276">
                <h3 className="text-lg font-semibold text-navy mb-2" data-id="element-277">
                  Independence over dependency
                </h3>
                <p className="text-text" data-id="element-278">
                  If I still have to sit in the middle of the solution, it is
                  not done.
                </p>
              </div>
            </div>

            <div className="mt-8" data-id="element-279">
              <Link to="/how-we-fix-it" className="inline-flex items-center space-x-2 text-seaglass hover:text-seaglass-light font-medium transition-colors duration-300" data-id="element-280">
                <span data-id="element-281">See the full approach: How We Fix It</span>
                <ArrowRight className="w-4 h-4" data-id="element-282" />
              </Link>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* WHAT WORKING TOGETHER FEELS LIKE */}
      <section className="py-24 px-6 bg-paper" data-id="element-283">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-284">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-285">
            <h2 className="text-3xl md:text-4xl font-serif font-semibold text-navy mb-8" data-id="element-286">
              What working together feels like
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6" data-id="element-287">
            <p className="text-body text-navy font-semibold" data-id="element-288">
              Direct. Calm. Clear.
            </p>
            <p className="text-body text-text" data-id="element-289">
              I am not coming in to become the long-term operator, run a giant
              transformation, or build a new layer of management theater.
            </p>
            <p className="text-body text-text" data-id="element-290">
              I am there to help you see the problem more clearly, make the
              right call, and, if it makes sense, redesign one meaningful part
              of the business so leaders spend less time translating and
              rescuing and more time leading.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* CLOSING CTA */}
      <section className="relative py-24 px-6 grain overflow-hidden bg-paper-dark" data-id="element-291">
        <div className="absolute right-[-2%] bottom-16 opacity-[0.06] pointer-events-none hidden lg:block" data-id="element-292">
          <BrandRays origin="bottom-right" rayCount={4} size={340} data-id="element-293" />
        </div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-6xl mx-auto" data-id="element-294">
          <div className="grid lg:grid-cols-[5fr_7fr] gap-0 items-stretch" data-id="element-295">
            {/* Left: Photo */}
            <motion.div variants={fadeIn} className="relative flex items-stretch lg:pr-16 mb-12 lg:mb-0" data-id="element-296">
              <div className="absolute top-6 left-0 w-[85%] h-[92%] bg-seaglass/10 border border-seaglass/15" data-id="element-297" />
              <div className="absolute bottom-0 right-12 w-16 h-1 bg-sand/60" data-id="element-298" />

              <div className="relative z-10 ml-6 mt-6 w-full" data-id="element-299">
                <div className="relative" data-id="element-300">
                  <div className="absolute -inset-[6px] border border-seaglass/25" data-id="element-301" />
                  <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/nmtzzfh8cozmkaychcuzkb/a0ec6fac-7fa4-4bbc-a56f-9030015af40e/Westley_Family_25_stevecowellphoto-178.webp" alt="John Westley" className="w-full aspect-[3/4] object-cover object-top grayscale block" data-id="element-302" />
                  <div className="absolute bottom-4 right-4 w-8 h-8 border-b border-r border-sand/50" data-id="element-303" />
                  <div className="absolute top-4 left-4 w-8 h-8 border-t border-l border-seaglass/40" data-id="element-304" />
                </div>
              </div>
            </motion.div>

            {/* Right: CTA */}
            <motion.div variants={fadeIn} className="flex flex-col justify-center lg:pl-4 lg:border-l lg:border-seaglass/15" data-id="element-305">
              <div className="mb-8" data-id="element-306">
                <div className="w-12 h-px bg-sand mb-5" data-id="element-307" />
                <p className="eyebrow text-seaglass tracking-widest" data-id="element-308">
                  Start here
                </p>
              </div>

              <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy leading-tight mb-6" data-id="element-309">
                If growth is still working but the business feels heavier than
                it should, start here.
              </h2>

              <div className="space-y-3" data-id="element-310">
                <Link to="/book" className="group inline-flex items-center justify-between w-full sm:w-auto sm:min-w-[340px] px-8 py-5 bg-navy hover:bg-sand text-white hover:text-navy font-semibold transition-all duration-300 border border-navy hover:border-sand" data-id="element-311">
                  <span data-id="element-312">Book an Honest Conversation</span>
                  <ArrowRight className="w-5 h-5 ml-4 group-hover:translate-x-1 transition-transform duration-300" data-id="element-313" />
                </Link>
                <p className="text-sm text-text-muted" data-id="element-314">
                  45 minutes. No pitch. Just clarity.
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>
    </main>;
}